import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersClient } from 'api';
import type { UserLocationHistory } from 'api/models/api';
import type {
  ColorPalette,
  PublicCalendarCodeResult,
  User,
  UserAppearanceToEdit,
  UserDetail,
  UserToAdd,
  UserToEdit
} from 'api/models/mainservice';
import type { ApiError } from 'common/models';
import { getCompanyAndUserInfoKeyBuilder, GLOBAL_QUERY_STALE_TIME } from 'common/queries';
import { handleApiErrorWithNotification } from 'helpers/errorHandling';

import type { UserGroupsToManage, UserSkillsToManage } from './models';

const usersKey = 'users';
const userLocationKey = 'userLocation';
const userAvailableColorPaletteKey = 'userAvailableColorPalette';
const userDetailKey = 'userDetail';

export const getUsersQueryKeyBuilder = () => [usersKey];
export const getUserLocationQueryKeyBuilder = () => [userLocationKey];
export const getUserAvailableColorPaletteKeyBuilder = () => [userAvailableColorPaletteKey];

export const getUserDetailQueryKeyBuilder = (id: string) => [userDetailKey, id];

export const useGetUsersQuery = (enabled?: boolean) => {
  return useQuery<User[], ApiError>(getUsersQueryKeyBuilder(), () => usersClient.getUsers(), {
    staleTime: GLOBAL_QUERY_STALE_TIME,
    enabled: enabled
  });
};

export const useGetUserDetailQuery = (id: string) => {
  return useQuery<UserDetail, ApiError>(getUserDetailQueryKeyBuilder(id), () =>
    usersClient.getUserDetail(id)
  );
};

export const useGetUserLocationQuery = (id: string, selectedDay: string) => {
  return useQuery<UserLocationHistory, ApiError>(
    [getUserLocationQueryKeyBuilder(), id, selectedDay],
    () => usersClient.getUserLocationHistory(id, selectedDay),
    {
      keepPreviousData: true
    }
  );
};

export const useAddUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UserDetail, ApiError, UserToAdd>(usersClient.addUser, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(getUsersQueryKeyBuilder());
      await queryClient.invalidateQueries(getUserAvailableColorPaletteKeyBuilder());
    }
  });
};

export const useEditUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UserDetail, ApiError, { userId: string; values: UserToEdit }>(
    usersClient.editUser,
    {
      onSuccess: (data, { userId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          data
        );
      }
    }
  );
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(usersClient.deleteUser, {
    onSuccess: async () => await queryClient.invalidateQueries(getUsersQueryKeyBuilder()),
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useEnableUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(usersClient.enableUser, {
    onSuccess: async (data, userId) => {
      await queryClient.invalidateQueries(getUsersQueryKeyBuilder());
      queryClient.setQueryData<UserDetail | undefined>(
        getUserDetailQueryKeyBuilder(userId),
        (oldData) =>
          oldData && {
            ...oldData,
            isDisabled: false
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useDisableUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(usersClient.disableUser, {
    onSuccess: async (data, userId) => {
      await queryClient.invalidateQueries(getUsersQueryKeyBuilder());
      await queryClient.invalidateQueries(getCompanyAndUserInfoKeyBuilder());
      queryClient.setQueryData<UserDetail | undefined>(
        getUserDetailQueryKeyBuilder(userId),
        (oldData) =>
          oldData && {
            ...oldData,
            isDisabled: true
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useDeleteUserDeviceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { userId: string; deviceId: string }>(
    usersClient.deleteUserDevice,
    {
      onSuccess: (data, { userId, deviceId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          (oldData) =>
            oldData && {
              ...oldData,
              devices: oldData.devices.filter((device) => device.id !== deviceId)
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useEnableUserDeviceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { userId: string; deviceId: string }>(
    usersClient.enableUserDevice,
    {
      onSuccess: (data, { userId, deviceId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          (oldData) =>
            oldData && {
              ...oldData,
              devices: oldData.devices.map((device) => {
                if (device.id === deviceId) {
                  return {
                    ...device,
                    isDisabled: false
                  };
                }
                return device;
              })
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useDisableUserDeviceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { userId: string; deviceId: string }>(
    usersClient.disableUserDevice,
    {
      onSuccess: (data, { userId, deviceId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          (oldData) =>
            oldData && {
              ...oldData,
              devices: oldData.devices.map((device) => {
                if (device.id === deviceId) {
                  return {
                    ...device,
                    isDisabled: true
                  };
                }
                return device;
              })
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useEnableUserPublicCalendarMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<PublicCalendarCodeResult, ApiError, string>(
    usersClient.enableUserPublicCalendar,
    {
      onSuccess: (data, userId) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          (oldData) =>
            oldData && {
              ...oldData,
              publicCalendarCode: data.publicCalendarCode
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useDisableUserPublicCalendarMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>(usersClient.disableUserPublicCalendar, {
    onSuccess: (data, userId) => {
      queryClient.setQueryData<UserDetail | undefined>(
        getUserDetailQueryKeyBuilder(userId),
        (oldData) =>
          oldData && {
            ...oldData,
            publicCalendarCode: ''
          }
      );
    },
    onError: (error) => handleApiErrorWithNotification(error)
  });
};

export const useResetUserPublicCalendarMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<PublicCalendarCodeResult, ApiError, string>(
    usersClient.resetUserPublicCalendar,
    {
      onSuccess: (data, userId) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          (oldData) =>
            oldData && {
              ...oldData,
              publicCalendarCode: data.publicCalendarCode
            }
        );
      },
      onError: (error) => handleApiErrorWithNotification(error)
    }
  );
};

export const useManageUserGroupsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UserDetail, ApiError, { userId: string; values: UserGroupsToManage }>(
    usersClient.manageGroups,
    {
      onSuccess: (data, { userId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          data
        );
      }
    }
  );
};

export const useManageUserSkillsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<UserDetail, ApiError, { userId: string; values: UserSkillsToManage }>(
    usersClient.manageSkills,
    {
      onSuccess: (data, { userId }) => {
        queryClient.setQueryData<UserDetail | undefined>(
          getUserDetailQueryKeyBuilder(userId),
          data
        );
      }
    }
  );
};

export const useGetUserAvailableColorPaletteQuery = () => {
  return useQuery<ColorPalette, ApiError>({
    queryKey: getUserAvailableColorPaletteKeyBuilder(),
    queryFn: () => usersClient.nextAvailableColor(),
    refetchOnWindowFocus: false
  });
};

export const useDeleteUserProfilePictureMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, string>({
    mutationFn: (userId) => usersClient.deleteUserProfilePicture(userId),
    onSuccess: (data, userId) => queryClient.invalidateQueries(getUserDetailQueryKeyBuilder(userId))
  });
};

export const useChangeUserAppearanceMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<undefined, ApiError, { userId: string; appearance: UserAppearanceToEdit }>({
    mutationFn: ({ userId, appearance }) => usersClient.changeUserAppearance(userId, appearance),
    onSuccess: async (data, { userId }) => {
      await queryClient.invalidateQueries(getUserDetailQueryKeyBuilder(userId));
      await queryClient.invalidateQueries(getUsersQueryKeyBuilder());
    }
  });
};
